import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { SelectType } from "@/enum/list.enum";

interface UseBulkDeepResearchHandlersParams {
	openSnackbar: (text: string) => void;
	selectAll: boolean;
	toggleSelectAll: () => void;
	setSelectType: (selectType: SelectType) => void;
}

interface BulkDeepResearchHandlers {
	handleDeepResearchSuccess: (result: { queued: number; skipped: number; total: number }) => void;
	handleDeepResearchError: (err: { status?: number; data?: { message?: string } }) => void;
}

/**
 * Success/error snackbar handlers for the bulk deep-research action, plus the post-success selection
 * cleanup. Extracted from UnassignedTable so that large file need not grow.
 */
export const useBulkDeepResearchHandlers = ({
	openSnackbar,
	selectAll,
	toggleSelectAll,
	setSelectType,
}: UseBulkDeepResearchHandlersParams): BulkDeepResearchHandlers => {
	const { t } = useTranslation();

	const handleDeepResearchSuccess = useCallback(
		(result: { queued: number; skipped: number; total: number }) => {
			openSnackbar(
				t("inbox.bulkDeepResearchSuccess", { queued: result.queued, skipped: result.skipped })
			);

			if (selectAll) {
				toggleSelectAll();
			}

			setSelectType(SelectType.CURRENT_PAGE);
		},
		[t, openSnackbar, selectAll, toggleSelectAll, setSelectType]
	);

	const handleDeepResearchError = useCallback(
		(err: { status?: number; data?: { message?: string } }) => {
			if (err.status === 402) {
				openSnackbar(t("inbox.bulkDeepResearchInsufficientCredits"));
			} else if (err.status === 404) {
				openSnackbar(t("inbox.bulkDeepResearchNoPrompt"));
			} else {
				openSnackbar(t("inbox.bulkDeepResearchError"));
			}
		},
		[t, openSnackbar]
	);

	return { handleDeepResearchSuccess, handleDeepResearchError };
};
